'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { VERIFICATION_COPY, FARM_EQUIPMENT_LABEL, GRAIN_FEED_LABEL } from '@/lib/features';
import { Beef, Wheat, Tractor, Wrench, HardHat, Truck, Gavel, Sprout } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import { HOME_HERO_PICTURE } from '@/lib/homeHero';

// Eight tiles, not ten: "Grain & Feed" and "Crops & Feed" were two labels on the
// same page, and "Supplies" pointed at a category the marketplace does not have
// as a tab (it lands under Deadstock/Farm Equipment, which is already here).
const quickCategories = [
  { label: 'Livestock', Icon: Beef, href: '/marketplace-listings?cat=livestock' },
  { label: GRAIN_FEED_LABEL, Icon: Wheat, href: '/crop-feed-bedding' },
  { label: 'Machinery', Icon: Tractor, href: '/marketplace-listings?cat=machinery' },
  { label: 'Machinery Hire', Icon: HardHat, href: '/machinery-hire' },
  { label: FARM_EQUIPMENT_LABEL, Icon: Wrench, href: '/marketplace-listings?cat=farm-equipment' },
  { label: 'Haulage', Icon: Truck, href: '/haulage-job-board' },
  { label: 'Wanted', Icon: Sprout, href: '/buyer-requests' },
  { label: 'Auctions', Icon: Gavel, href: '/timed-auctions' },
];

const trustLogos = [
  'Red Tractor',
  'AHDB',
  'NFU',
  'RSPCA Assured',
  'Quality Meat Scotland',
];

export default function HomepageHero() {
  const [searchQuery, setSearchQuery] = useState('');
  const router = useRouter();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(`/marketplace-listings?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <>
      {/* Hero */}
      <section className="relative bg-primary overflow-hidden">
        {/* Background texture */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, #ffffff22 0%, transparent 50%), radial-gradient(circle at 80% 20%, #ffffff15 0%, transparent 40%)`,
          }} />
        </div>

        {/* The photograph, when there is one.

            Shown from 1280px up only. Below that the headline column already
            reaches across the whole hero, so a picture behind it would be a
            legibility problem rather than a picture. A separate mobile band is
            a design decision, not an oversight.

            The headline overlaps the left edge of this picture on a 1280-wide
            screen, so that edge is taken back to solid green: the gradient is
            opaque across the first tenth, which is where the headline column
            overlaps it, and clear by 60%. Every line of hero text sits inside
            the opaque part — measured against a pure white stand-in, which is
            the worst case a photograph can be for light text. */}
        {HOME_HERO_PICTURE && (
          <div className="hidden xl:block absolute inset-y-0 right-0 w-[38%] 2xl:w-1/2 max-w-[600px]">
            <AppImage
              src={HOME_HERO_PICTURE.url}
              alt={HOME_HERO_PICTURE.alt}
              fill
              priority
              sizes="(max-width: 1279px) 1px, (min-width: 1536px) 50vw, 38vw"
              className="object-cover object-center"
            />
            {/* Ends on the green at zero alpha rather than on `transparent`.
                `transparent` is transparent BLACK, which some browsers fade
                through on the way, and a grey bruise across the middle of a
                photograph is not what this is for. */}
            <div className="absolute inset-0 bg-gradient-to-r from-primary from-10% via-primary/40 via-35% to-primary/0 to-60%" />
          </div>
        )}

        <div className="relative max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-14 md:py-20">
          <div className="max-w-3xl">
            {/* Eyebrow */}
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 mb-5">
              <span className="w-2 h-2 rounded-full bg-green-300 animate-pulse" />
              <span className="text-green-200 text-xs font-semibold tracking-wide uppercase">The UK&apos;s Trusted Online Marketplace for Farming</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-3">
              Buy Better. Sell Smarter.<br />
              <span className="text-green-300">Trade Direct.</span>
            </h1>

            <p className="text-green-100 text-lg md:text-xl mb-3 leading-relaxed max-w-2xl">
              <strong className="text-white">FarmTradeDirect</strong> is the UK&apos;s modern online marketplace connecting farmers, livestock producers, contractors, merchants and rural businesses through one secure, easy-to-use trading platform.
            </p>

            <p className="text-green-200 text-sm md:text-base mb-6 leading-relaxed max-w-2xl">
              Buy and sell <strong className="text-green-100">livestock, grain, machinery, equipment and agricultural supplies</strong> — direct, simple and commission-free.
            </p>

            {/* Search bar */}
            <form onSubmit={handleSearch} className="flex gap-2 mb-6 max-w-2xl">
              <div className="relative flex-1">
                <Icon name="MagnifyingGlassIcon" size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search livestock, machinery, grain, equipment…"
                  className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white text-foreground placeholder-muted-foreground text-sm font-medium focus:outline-none focus:ring-2 focus:ring-green-300 shadow-lg"
                />
              </div>
              <button
                type="submit"
                className="bg-accent hover:bg-accent/90 text-white font-bold px-6 py-3.5 rounded-xl transition-colors shadow-lg shrink-0"
              >
                Search
              </button>
            </form>

            {/* CTA buttons */}
            <div className="flex flex-wrap gap-3 mb-5">
              <Link
                href="/sign-up-login-screen"
                className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-white font-bold px-6 py-3 rounded-xl transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
              >
                <Icon name="UserPlusIcon" size={18} />
                Register Today
              </Link>
              <Link
                href="/post-listing"
                className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 border border-white/30 text-white font-bold px-6 py-3 rounded-xl transition-all"
              >
                <Icon name="PlusCircleIcon" size={18} />
                List Your First Item
              </Link>
              <Link
                href="/marketplace-listings"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold px-6 py-3 rounded-xl transition-all"
              >
                Browse Listings
                <Icon name="ArrowRightIcon" size={16} />
              </Link>
            </div>

            <p className="text-green-300 text-xs font-medium">
              Register today. List your first item. Start trading with confidence.
            </p>
          </div>

          {/* Stats strip.
              "12,000+ Active listings" used to lead this row. Alex asked for it
              off until the site is more mature — it was a number the generator
              invented, and a visitor who then finds a handful of listings has
              been told something untrue. Put it back by re-adding one line. */}
          <div className="mt-10 flex flex-wrap gap-6 md:gap-10">
            {[
              { value: '0%', label: 'Commission charged' },
              { value: '100%', label: 'Verified members' },
              { value: '48 counties', label: 'UK coverage' },
            ].map((stat) => (
              <div key={stat.label} className="flex flex-col">
                <span className="text-2xl font-extrabold text-white">{stat.value}</span>
                <span className="text-green-300 text-xs font-medium">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* "Trusted by" bar */}
      <section className="bg-white border-b border-border py-4 px-4">
        <div className="max-w-screen-2xl mx-auto flex flex-col sm:flex-row items-center gap-4 sm:gap-8">
          <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest shrink-0">Trusted by</span>
          <div className="flex flex-wrap items-center gap-4 sm:gap-8">
            {trustLogos.map((logo) => (
              <span key={logo} className="text-sm font-semibold text-muted-foreground/70 hover:text-foreground transition-colors">
                {logo}
              </span>
            ))}
          </div>
          <div className="sm:ml-auto flex items-center gap-2 text-xs text-muted-foreground">
            <Icon name="ShieldCheckIcon" size={14} className="text-primary" />
            <span>{VERIFICATION_COPY.badgeLine}</span>
          </div>
        </div>
      </section>

      {/* Quick category browse */}
      <section className="bg-muted/40 border-b border-border py-6 px-4">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-sm font-bold text-foreground uppercase tracking-wider">Everything Agriculture. One Marketplace.</h2>
          </div>
          <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
            {quickCategories.map((cat) => (
              <Link
                key={cat.label}
                href={cat.href}
                className="flex flex-col items-center gap-1.5 bg-card hover:bg-primary hover:text-white border border-border hover:border-primary rounded-xl p-3 transition-all duration-200 group"
              >
                <cat.Icon className="w-7 h-7 text-primary group-hover:text-white transition-colors" strokeWidth={1.75} aria-hidden="true" />
                <span className="text-xs font-semibold text-foreground group-hover:text-white text-center leading-tight">{cat.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
